import { ReadonlyVec3, vec3 } from 'gl-matrix';
import {
    dcmjsCreateNewStructure,
    dcmjsGetString,
    dcmjsRemoveStructure,
    dcmjsSetString,
    dcmjsUpdateContourSequence,
} from '../services/dicomProcessor';
import type { RoiId } from '../changes/ids';
import { DuplicateExternalRoiError } from '../errors/DomainError';
import { DcmJsDatasetWrapper } from './dcmJsWrapper';
import { DicomBase } from './dicomBase';
import { DICOMApprovalStatus } from './dicomConstants';
import { DicomRoi } from './dicomRoi';
import { Modality } from './modality';
import { getDICOMDate, getDICOMTime } from './utils';

export enum ContourGeometricType {
    POINT = 'POINT',
    OPEN_PLANAR = 'OPEN_PLANAR',
    OPEN_NONPLANAR = 'OPEN_NONPLANAR',
    CLOSED_PLANAR = 'CLOSED_PLANAR',
}

export enum RoiGenerationAlgorithmType {
    MANUAL = 'MANUAL',
    AUTOMATIC = 'AUTOMATIC',
    SEMI_AUTOMATIC = 'SEMI_AUTOMATIC',
    UNKNOWN = '',
}

export enum RoiInterpretedType {
    CTV = 'CTV', // Clinical Target Volume
    GTV = 'GTV', // Gross Tumor Volume
    PTV = 'PTV', // Planning Target Volume
    EXTERNAL = 'EXTERNAL', // External patient contour, the BODY contour
    BOLUS = 'BOLUS', // patient bolus to be used for external beam therapy
    SUPPORT = 'SUPPORT', // external patient support device
    FIXATION = 'FIXATION', // external patient fixation or immobilization device
    CONTROL = 'CONTROL', // ROI to be used in control of dose optimization and calculation
    ORGAN = 'ORGAN', // patient organ
    OAR = 'OAR', // Organ at Risk
    MARKER = 'MARKER', // patient marker or marker on a localizer
    AVOIDANCE = 'AVOIDANCE', // region in which dose is to be minimized
    TREATED_VOLUME = 'TREATED_VOLUME', // Treated Volume
    IRRAD_VOLUME = 'IRRAD_VOLUME', // Irradiated Volume
    CONTRAST_AGENT = 'CONTRAST_AGENT', // volume into which a contrast agent has been injected
    REGISTRATION = 'REGISTRATION', // registration ROI
    ISOCENTER = 'ISOCENTER', // treatment isocenter to be used for external beam therapy
    CAVITY = 'CAVITY', // patient anatomical cavity
    BRACHY_CHANNEL = 'BRACHY_CHANNEL', // brachytherapy channel
    BRACHY_ACCESSORY = 'BRACHY_ACCESSORY', // brachytherapy accessory device
    BRACHY_SRC_APP = 'BRACHY_SRC_APP', // brachytherapy source applicator
    BRACHY_CHNL_SHLD = 'BRACHY_CHNL_SHLD', // brachytherapy channel shield
    DOSE_REGION = 'DOSE_REGION', // ROI to be used as a dose reference
    DOSE_MEASUREMENT = 'DOSE_MEASUREMENT', // ROI representing a dose measurement device, such as a chamber or TLD
    DEVICE = 'DEVICE', // device not addressed by another Defined Term
    UNKNOWN = '', // Unknown
}

export const ROI_TARGET_TYPES = [
    RoiInterpretedType.CTV,
    RoiInterpretedType.GTV,
    RoiInterpretedType.PTV,
];

export const ROI_OAR_TYPES = [
    RoiInterpretedType.EXTERNAL,
    RoiInterpretedType.ORGAN,
    RoiInterpretedType.OAR,
    RoiInterpretedType.CAVITY,
];

export const AVAILABLE_ROI_TYPES = [
    ...ROI_OAR_TYPES,
    ...ROI_TARGET_TYPES,
    RoiInterpretedType.AVOIDANCE,
    RoiInterpretedType.CONTROL,
    RoiInterpretedType.MARKER,
];

export const ROI_POINT_TYPES = [
    RoiInterpretedType.MARKER,
    RoiInterpretedType.ISOCENTER,
    RoiInterpretedType.REGISTRATION,
];

export type RoiIdentificationCodeSequence = {
    codeValue: string;
    codingSchemeDesignator: string;
    codingSchemeVersion: string;
    codeMeaning: string;
};

export class DicomContour {
    public constructor(
        public readonly type: ContourGeometricType,
        public readonly data: ReadonlyArray<ReadonlyVec3>,
        public readonly referencedSOPClassUID?: string,
        public readonly referencedSOPInstanceUID?: string
    ) {
        if (referencedSOPInstanceUID) {
            this.referencedSOPInstanceUID = referencedSOPInstanceUID.trim();
        }
        if (referencedSOPClassUID) {
            this.referencedSOPClassUID = referencedSOPClassUID.trim();
        }
    }

    public get length() {
        return this.data.length;
    }

    public get isValid(): boolean {
        if (this.length > 2) {
            //This is not ideal, but has to be done
            const a = this.data[0] as vec3;
            const delta = vec3.create();
            const dir = vec3.normalize(
                vec3.create(),
                vec3.subtract(delta, this.data[1] as vec3, a)
            );
            for (let i = 2; i < this.data.length; ++i) {
                vec3.subtract(delta, this.data[i] as vec3, a);
                vec3.normalize(delta, delta);
                const dot = vec3.dot(dir, delta);
                if (1.0 - Math.abs(dot) > 1e-6) {
                    return true;
                }
            }
        }
        return false;
    }

    /**
     * Returns a new DicomContour with updated Referenced SOP UIDs.
     * Contour vertex data is shared (not cloned) since it is ReadonlyArray.
     */
    public withReferencedUids(
        referencedSOPClassUID: string,
        referencedSOPInstanceUID: string
    ): DicomContour {
        return new DicomContour(
            this.type,
            this.data,
            referencedSOPClassUID,
            referencedSOPInstanceUID
        );
    }
}

export class DicomStructureSet extends DicomBase implements Iterable<DicomRoi> {
    public frameOfReferenceUid: string;

    /** The ROIs by {@link DicomRoi.uid}, in the order the file holds them. */
    public rois: Map<RoiId, DicomRoi>;

    /**
     * (0008,1155) Referenced SOP Instance UID Attribute
     * from (3006,0016) Contour Image Sequence Attribute
     * TODO: or alternatively could also get from (3006,0010) Referenced Frame of Reference Sequence Attribute
     */
    public readonly referencedSopUids: Set<string>;

    /**
     * (0008,1150) Referenced SOP Class UID Attribute
     */
    public readonly referencedSopClassUid: string;

    public readonly referencedSeriesUid: string;

    public readonly seriesInstanceUid: string;

    constructor(
        dataset: DcmJsDatasetWrapper,
        sopUid: string,
        frameOfReferenceUid: string,
        referencedSeriesUid: string,
        rois: Map<RoiId, DicomRoi> | undefined = undefined,
        referencedSopUids: string[] = [],
        referencedSopClassUid: string,
        seriesInstanceUid: string
    ) {
        super(sopUid, Modality.RTSTRUCT, dataset);
        this.frameOfReferenceUid = frameOfReferenceUid;
        this.referencedSeriesUid = referencedSeriesUid;
        this.rois = rois ?? new Map<RoiId, DicomRoi>();
        this.referencedSopUids = new Set(referencedSopUids);
        this.referencedSopClassUid = referencedSopClassUid;
        this.seriesInstanceUid = seriesInstanceUid;
    }

    public get numberOfStructures(): number {
        return this.rois.size;
    }

    public addRoi(roi: DicomRoi): void {
        this._ensureCanModify();
        this.rois.set(roi.uid, roi);
    }

    public getRoi(uid: RoiId): DicomRoi | undefined {
        return this.rois.get(uid);
    }

    /**
     * Returns the smallest positive integer the file does not hold an ROI
     * under, for a new ROI's items in the file.
     */
    public getNextFreeRoiNumber(): number {
        const used = new Set(
            Array.from(this.rois.values(), (roi) => roi.roiNumberInFile)
        );
        let roiNumber = 1;

        while (used.has(roiNumber)) {
            roiNumber++;
        }

        return roiNumber;
    }

    /**
     * Gives each ROI the identity `byRoiNumber` names for the number the file
     * holds it under, and re-keys {@link rois} under the new identities in
     * the file's order. An ROI the map does not name keeps the
     * identity it has. Refuses, before changing anything, a map that would
     * leave two ROIs with one identity.
     */
    public adoptRoiUids(byRoiNumber: ReadonlyMap<number, RoiId>): void {
        const rekeyed = new Map<RoiId, DicomRoi>();
        for (const roi of this.rois.values()) {
            const uid = byRoiNumber.get(roi.roiNumberInFile) ?? roi.uid;
            if (rekeyed.has(uid)) {
                throw new Error(
                    'The stored structure identities name one identity for two ROIs'
                );
            }
            rekeyed.set(uid, roi);
        }
        for (const [uid, roi] of rekeyed) {
            roi.adoptUid(uid);
        }
        this.rois = rekeyed;
    }

    public clearRois() {
        this._ensureCanModify();
        this.rois.clear();
    }

    /**
     * Update the contour sequence with the ROIs contour data.
     */
    public updateContourSequence(uid: RoiId) {
        const roi = this.rois.get(uid);
        if (roi) {
            dcmjsUpdateContourSequence(
                this.datasetWrapper,
                roi.roiNumberInFile,
                roi.contours
            );
        }
    }

    /**
     * Structure Set Label Attribute Tag (3006,0002)
     */
    public get structureSetLabel(): string {
        return dcmjsGetString(this.datasetWrapper, 'StructureSetLabel');
    }

    public set structureSetLabel(label: string) {
        this._ensureCanModify();
        dcmjsSetString(this.datasetWrapper, 'StructureSetLabel', label);
    }

    /**
     * Structure Set Description Attribute Tag (3006,0006)
     */
    public get structureSetDescription(): string {
        return dcmjsGetString(this.datasetWrapper, 'StructureSetDescription');
    }

    public set structureSetDescription(description: string) {
        this._ensureCanModify();
        dcmjsSetString(
            this.datasetWrapper,
            'StructureSetDescription',
            description
        );
    }

    private _ensureCanModify() {
        if (this.isApproved) {
            throw new Error(
                `Cannot modify approved structure set '${this.structureSetLabel}'`
            );
        }
    }

    public get isApproved(): boolean {
        return this.approvalStatus === DICOMApprovalStatus.APPROVED;
    }

    /**
     * Approval Status Attribute Tag (300E,0002)
     */
    public get approvalStatus(): DICOMApprovalStatus {
        return dcmjsGetString(
            this.datasetWrapper,
            'ApprovalStatus'
        ) as DICOMApprovalStatus;
    }

    public set approvalStatus(status: DICOMApprovalStatus) {
        dcmjsSetString(this.datasetWrapper, 'ApprovalStatus', status);
    }

    /**
     * Reviewer Name Attribute Tag (300E,0008), VR: PN, as text. A name with
     * no alphabetic part, or no name, is the empty string.
     */
    public get reviewerName(): string {
        return dcmjsGetString(this.datasetWrapper, 'ReviewerName');
    }

    public set reviewerName(name: string) {
        dcmjsSetString(this.datasetWrapper, 'ReviewerName', name);
    }

    /**
     * Review Date Attribute Tag (300E,0004)
     */
    public get reviewDate(): string {
        return dcmjsGetString(this.datasetWrapper, 'ReviewDate');
    }

    public set reviewDate(reviewDate: string) {
        dcmjsSetString(this.datasetWrapper, 'ReviewDate', reviewDate);
    }

    /**
     * Review Time Attribute Tag (300E,0005)
     */
    public get reviewTime(): string {
        return dcmjsGetString(this.datasetWrapper, 'ReviewTime');
    }

    public set reviewTime(reviewTime: string) {
        dcmjsSetString(this.datasetWrapper, 'ReviewTime', reviewTime);
    }

    /**
     * Sets approval status with review dates (mutable version).
     * @param approvalStatus The new approval status
     * @param reviewerName The reviewer name
     */
    public setApprovalStatus(
        approvalStatus: DICOMApprovalStatus,
        reviewerName: string
    ): void {
        // APPROVED and REJECTED are both review outcomes: each carries the
        // reviewer and the review timestamp (clinical sign-off must be
        // attributable — the connector's approval endpoint enforces the
        // same rule). UNAPPROVED clears all three.
        const isReviewed =
            approvalStatus === DICOMApprovalStatus.APPROVED ||
            approvalStatus === DICOMApprovalStatus.REJECTED;

        let reviewDate = '';
        let reviewTime = '';
        if (isReviewed) {
            // Stamp the moment of THIS review outcome. The existing
            // timestamp is kept only for a same-status re-write; a status
            // change (e.g. approve → reject) is a new sign-off and must
            // not carry the previous outcome's date/time. Date and time
            // are decided together so they never mix two moments.
            const isSameOutcome =
                this.approvalStatus === approvalStatus &&
                this.reviewDate !== '' &&
                this.reviewTime !== '';
            const now = new Date();
            reviewDate = isSameOutcome ? this.reviewDate : getDICOMDate(now);
            reviewTime = isSameOutcome ? this.reviewTime : getDICOMTime(now);
        }

        // Update the DICOM dataset directly
        dcmjsSetString(this.datasetWrapper, 'ApprovalStatus', approvalStatus);
        dcmjsSetString(
            this.datasetWrapper,
            'ReviewerName',
            isReviewed ? reviewerName : ''
        );
        dcmjsSetString(this.datasetWrapper, 'ReviewDate', reviewDate);
        dcmjsSetString(this.datasetWrapper, 'ReviewTime', reviewTime);
    }

    /**
     * The EXTERNAL (body contour) ROI of this structure set, or undefined.
     * `excludeUid` lets a retype check ignore the ROI being retyped itself
     * (re-asserting EXTERNAL on the existing EXTERNAL is legal).
     */
    public findExternalRoi(excludeUid?: RoiId): DicomRoi | undefined {
        for (const roi of this.rois.values()) {
            if (
                roi.uid !== excludeUid &&
                roi.interpretedType === RoiInterpretedType.EXTERNAL
            ) {
                return roi;
            }
        }
        return undefined;
    }

    /**
     * Rejects a mutation that would leave this set with two EXTERNAL ROIs.
     * Called before any dataset write so a rejected mutation leaves the
     * set untouched (validation must strictly precede writes — the
     * tracker's rollback covers pending changesets, not applied ones).
     */
    public ensureExternalUnique(
        interpretedType: RoiInterpretedType | undefined,
        excludeUid?: RoiId
    ) {
        if (interpretedType !== RoiInterpretedType.EXTERNAL) {
            return;
        }
        const existing = this.findExternalRoi(excludeUid);
        if (existing) {
            throw new DuplicateExternalRoiError(
                `Structure set '${this.structureSetLabel}' already contains ` +
                    `an EXTERNAL structure ('${existing.name}')`
            );
        }
    }

    /***
     * Creates a new ROI into this structure set.
     */
    public createRoi(
        interpretedType?: RoiInterpretedType,
        structureName: string = ''
    ): DicomRoi {
        this._ensureCanModify();
        this.ensureExternalUnique(interpretedType);
        // create a new default ROI into this structure set, including
        // inserting it into the DICOM dataset wrapper
        const roi = dcmjsCreateNewStructure(this);

        if (structureName !== '') {
            roi.name = structureName;
        } else {
            const isPoint =
                interpretedType !== undefined &&
                ROI_POINT_TYPES.includes(interpretedType);
            roi.name = this.generateNumberedName(isPoint ? 'POI' : 'ROI');
        }

        if (interpretedType) {
            roi.interpretedType = interpretedType;
        }

        return roi;
    }

    /**
     * Generates a unique numbered name like "ROI_1", "ROI_2" or
     * "POI_1", "POI_2" by finding the first unused suffix.
     */
    private generateNumberedName(baseName: string): string {
        const existingNames = new Set(
            Array.from(this.rois.values()).map((r) => r.name)
        );
        for (let i = 1; i < 1000; i++) {
            const candidate = `${baseName}_${i}`;
            if (!existingNames.has(candidate)) {
                return candidate;
            }
        }
        return `${baseName}_${Date.now()}`;
    }

    /**
     * Deletes an ROI directly
     */
    public deleteRoi(uid: RoiId): void {
        this._ensureCanModify();
        const roi = this.rois.get(uid);
        if (!roi) {
            return; // ROI doesn't exist, nothing to delete
        }

        this.rois.delete(uid);
        dcmjsRemoveStructure(this.datasetWrapper, roi.roiNumberInFile);
    }

    /**
     * Manufacturer Attribute Tag (0008,0070)
     */
    public get manufacturer(): string {
        return dcmjsGetString(this.datasetWrapper, 'Manufacturer');
    }

    /**
     * Manufacturer's Model Name Attribute Tag (0008,1090)
     */
    public get manufacturerModelName(): string {
        return dcmjsGetString(this.datasetWrapper, 'ManufacturerModelName');
    }

    /**
     * Software Versions Attribute Tag (0018,1020), every value in order (it
     * is multi-valued: the product version, then each AI model's name and
     * version), as one text separated by the DICOM value delimiter.
     */
    public get softwareVersions(): string {
        return dcmjsGetString(this.datasetWrapper, 'SoftwareVersions');
    }

    [Symbol.iterator](): Iterator<DicomRoi, DicomRoi, undefined> {
        return this.rois.values() as Iterator<DicomRoi, DicomRoi, undefined>;
    }

    /**
     * Compares two structure names using consistent domain rules.
     */
    static structureNamesEqual(a: string, b: string): boolean {
        return a.trim().toLowerCase() === b.trim().toLowerCase();
    }

    /**
     * Compares two structure names using case-sensitive trimmed comparison.
     */
    static structureNamesTrimmedEqual(a: string, b: string): boolean {
        return (
            DicomStructureSet.trimmedNameKey(a) ===
            DicomStructureSet.trimmedNameKey(b)
        );
    }

    /** The form of a name that {@link structureNamesTrimmedEqual} compares. */
    static trimmedNameKey(name: string): string {
        return name.trim();
    }

    /**
     * Whether an ROI's name matches case-sensitively after whitespace
     * trimming. Structures with different casing coexist ("Body" and
     * "BODY"), but whitespace variations match ("Brain" and "  Brain  ").
     */
    hasRoiWithTrimmedName(name: string): boolean {
        return Array.from(this.rois.values()).some((roi) =>
            DicomStructureSet.structureNamesTrimmedEqual(roi.name, name)
        );
    }
}
